<template>
  <dn-collection
    :cid="placeholderId"
    :class="['dn-edition-kilkaya-collection', backgroundColor?.toLowerCase()]"
    provider="kilkaya"
    :options="{ streamId, size, removeUsed }"
    :fnc="addSkipIds"
    :data-streamId="streamId"
  >
    <template #default="{ collection, loading }">
      <dn-headline
        v-if="headline"
        class="header"
        :text="headline"
      />
      <dn-grid-layout
        v-if="loading"
        :pattern="activeLayouts"
        :repeat="1"
        :items="skeletonItems"
      >
        <template #default="{ itemIndex }">
          <dn-skeleton :show-media="!imageVisibility(itemIndex)" />
        </template>
      </dn-grid-layout>
      <dn-set
        v-for="(group, groupIndex) in buildSetGroups(collection, slots)"
        v-else
        :key="groupIndex"
        :layout="group.layout"
        :items="group.items"
        :theme_layout="backgroundColor"
      />
    </template>
  </dn-collection>
</template>
<script setup lang="ts">
import { provide } from 'vue';
import type { CardProps, KilkayaItem, SetGroup, Slot } from '~/types/collection';

const props = defineProps({
  placeholder: {
    type: String,
    required: true
  },
  streamId: {
    type: String,
    required: true
  },
  headline: {
    type: String,
    required: false,
    default: null
  },
  rowlayout: {
    type: String,
    required: false,
    default: 'BBB'
  },
  repeat: {
    type: Number,
    required: false,
    default: 1
  },
  id: {
    type: String,
    required: true
  },
  hideLead: {
    type: Boolean,
    required: false,
    default: true
  },
  backgroundColor: {
    type: String,
    required: false,
    default: null
  },
  removeUsed: {
    type: Boolean,
    required: false,
    default: true
  },
  assetDefaultRatio: {
    type: String,
    required: false,
    default: ''
  },
  imageHide: {
    type: Boolean,
    required: false,
    default: false
  },
  type: {
    type: String,
    required: false,
    default: null
  },
  slots: {
    type: Array as PropType<Slot[]>,
    required: false,
    default: () => []
  }
});

provide('teaser_provider', 'articleTeaser');
const globalSkipIds = inject('global_skip_ids');
const placeholderId = `${props.placeholder}__${props.streamId}__${props.id}`;

const { activeLayouts, trimLayouts } = getRowLayout(props.rowlayout, props.repeat);

const size = activeLayouts.reduce((sum, layout) => sum + layoutItemCount(layout), 0);

const skeletonItems = Array.from({ length: size }, (_, index) => ({ id: `skeleton-${index}` }));

const addSkipIds = (items: { id: string }[]) => {
  globalSkipIds.value.push(...items.map(item => item.id));
};

const isSpecialLayout = activeLayouts.includes('bbbabbb');
const imageVisibility = (itemIndex: number): boolean => (isSpecialLayout && itemIndex >= 3) || props.imageHide;

/*
 * Slice the kilkaya stream into one group per layout pattern so each pattern
 * renders its own <dn-set>. The global item index is preserved so image-hide
 * keeps working across rows. Cards self-fetch by id (prefetch), so only the
 * static card props are stamped here. The `slots` prop is built per row (via
 * calculateSetRows in slotInjectionProcessor), not per group, so each group
 * claims its own run of slots through a cursor: on mobile a multi-row layout
 * gets one ad slot per row, on desktop calculateSetRows is 1 so each group
 * gets a single slot. DnSet renders the slots via <dn-component>.
 */
const buildSetGroups = (collection: KilkayaItem[], slots: Slot[] = []) => {
  const groups: SetGroup[] = [];
  let index = 0;
  let slotCursor = 0;

  for (const layout of trimLayouts(collection)) {
    const slice = collection.slice(index, index + layoutItemCount(layout));
    if (slice.length === 0) { break; }

    const items: CardProps[] = slice.map((item, i) => ({
      id: item.id,
      type: item.type,
      assetDefaultRatio: props.assetDefaultRatio,
      hide_lead: props.hideLead,
      teaser_type: props.type,
      prefetch: true,
      loadingMode: 'lazy',
      ...(item.type !== 'video' ? { image_hide: imageVisibility(index + i) } : {})
    }));
    const rowCount = calculateSetRows({ layout, items: [] });
    items.push(...slots.slice(slotCursor, slotCursor + rowCount));
    slotCursor += rowCount;

    groups.push({ layout, items });
    index += slice.length;
  }

  return groups;
};

</script>

<style lang="scss">
.dn-edition-kilkaya-collection {
  --dn-collection_grid-template: var(--dn-edition-kilkaya-collection_grid-template);
  --dn-collection_gap: var(--dn-edition-kilkaya-collection_gap);
  --dn-collection_fill: var(--dn-edition-kilkaya-collection_fill);

  .dn-teaser-card {
    --dn-teaser-card--link-text-decoration: underline;
    --teaser-card--card_border-radius: 4px;
    --teaser-card--card_row-gap: 8px;
  }
}
</style>
